import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/commerce-pwa-studio/commerce-pwa-studio/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "internationalization"
    }}>{`Internationalization`}</h1>
    <p>{`Internationalization (i18n) is a feature that lets you localize content for a culture, region, or language.
This feature is often associated with localization (l10n), which is the process of transforming content for a specific locale.`}</p>
    <h2 {...{
      "id": "internationalization-in-adobe-commerce-and-magento-open-source-versus-pwa-studio"
    }}>{`Internationalization in Adobe Commerce and Magento Open Source versus PWA Studio`}</h2>
    <p>{`The Adobe Commerce and Magento Open Source applications include an i18n feature that provides translated text to the frontend theme.
This feature uses dictionary files inside language packages to provide translation data for the application when it renders a page.
The language packages themselves are extensions the application installs using Composer.`}</p>
    <p>{`For more information, see the core documentation topic: `}<a parentName="p" {...{
        "href": "https://developer.adobe.com/commerce/frontend-core/guide/translations/"
      }}>{`Translations overview`}</a>{`.`}</p>
    <p>{`The tight coupling between each applications' i18n feature and the frontend theme makes it difficult to use the same translation mechanisms in PWA Studio storefronts.
Instead, PWA Studio provides its own i18n feature that follows a similar design as the one in Adobe Commerce and Magento Open Source.`}</p>
    <h2 {...{
      "id": "how-it-works"
    }}>{`How it works`}</h2>
    <p>{`PWA Studio provides a context provider for translations called the `}<a parentName="p" {...{
        "href": "https://github.com/magento/pwa-studio/blob/develop/packages/venia-ui/lib/components/App/localeProvider.js"
      }}>{`LocaleProvider`}</a>{`.
This context provider contains translation data from dictionary files and supplies them to its child components.`}</p>
    <p>{`The i18n feature in PWA Studio is an implementation of the `}<a parentName="p" {...{
        "href": "https://formatjs.io/docs/react-intl/"
      }}>{`react-intl`}</a>{` library.
The `}<inlineCode parentName="p">{`LocaleProvider`}</inlineCode>{` component in PWA Studio wraps around the library's `}<a parentName="p" {...{
        "href": "https://formatjs.io/docs/react-intl/components/#intlprovider"
      }}>{`IntlProvider`}</a>{` and provides it with translation data.`}</p>
    <p>{`This library also provides `}<a parentName="p" {...{
        "href": "https://formatjs.io/docs/react-intl/api#formatmessage"
      }}>{`formatMessage()`}</a>{` and `}<a parentName="p" {...{
        "href": "https://formatjs.io/docs/react-intl/components#formattedmessage"
      }}>{`FormattedMessage`}</a>{` to localize text in child components under `}<inlineCode parentName="p">{`LocaleProvider`}</inlineCode>{`.
You must at least supply values for `}<inlineCode parentName="p">{`id`}</inlineCode>{` and a `}<inlineCode parentName="p">{`defaultMessage`}</inlineCode>{` fallback when you use either the function or component.`}</p>
    <p>{`The following code samples produce the same results:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`return (
  <p className={classes.text}>
    <FormattedMessage
      id={"component.loremIpsum"}
      defaultMessage={"Lorem Ipsum"}
    />
  </p>
);
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`const translatedText = formatMessage({
  id: "component.loremIpsum",
  defaultMessage: "Lorem Ipsum",
});

return <p className={classes.text}>{translatedText}</p>;
`}</code></pre>
    <p>{`The i18n feature uses the `}<inlineCode parentName="p">{`id`}</inlineCode>{` parameter to look up the localized text from the dictionary files, which the feature supplies to the `}<inlineCode parentName="p">{`LocaleProvider`}</inlineCode>{`.`}</p>
    <h2 {...{
      "id": "translation-dictionaries"
    }}>{`Translation dictionaries`}</h2>
    <p>{`Translation dictionary files contain key/value pairs for localized text.
PWA Studio's i18n feature uses a similar dictionary approach for translation files as Adobe Commerce and Magento Open Source, but
instead of a CSV format, it uses JSON.`}</p>
    <h3 {...{
      "id": "filename-format"
    }}>{`Filename format`}</h3>
    <p>{`Dictionary files must be inside an `}<inlineCode parentName="p">{`i18n`}</inlineCode>{` directory and use their target locale for their filename.`}</p>
    <p>{`Instead of the `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/IETF_language_tag"
      }}>{`standard ISO language tag`}</a>{` the filename format follow's the following convention for specifying locale:
`}<inlineCode parentName="p">{`<language id in lowercase>_<country id in uppercase>.json`}</inlineCode>{`.
For example: `}<inlineCode parentName="p">{`en_US.json`}</inlineCode>{`, `}<inlineCode parentName="p">{`en_GB.json`}</inlineCode>{`, `}<inlineCode parentName="p">{`fr_FR.json`}</inlineCode>{`.`}</p>
    <h3 {...{
      "id": "id-formats"
    }}>{`ID formats`}</h3>
    <p>{`The JSON object's keys act as unique IDs for localized text.
They map a placeholder string value in components to the actual rendered text.`}</p>
    <p>{`PWA Studio recommends and uses a `}<strong parentName="p">{`dot notation format`}</strong>{` in its components.
This format uses the component name and descriptor to form the ID value.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "accountMenu.orderHistoryLink": "Order History",
  "autocomplete.resultSummary": "{resultCount} items"
}
`}</code></pre>
    <p>{`This approach helps identify which component renders the text and provides a unique value for the ID.`}</p>
    <p>{`However, the i18n feature in PWA Studio does not limit you to the dot notation format.
For example, in Adobe Commerce and Magento Open Source, the original `}<inlineCode parentName="p">{`en_US`}</inlineCode>{` locale text identifies the translated text in the application.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "About Us": "About Us",
  "Hi, {name}": "Hi, {name}"
}
`}</code></pre>
    <p>{`PWA Studio's i18n feature allows you to use this notation in your own components and storefront.
Both approach have their pros and cons, and developers are free to choose which approach works for them when they develop their own components and storefronts.`}</p>
    <h3 {...{
      "id": "message-syntax"
    }}>{`Message syntax`}</h3>
    <p>{`The i18n feature accepts the same `}<a parentName="p" {...{
        "href": "https://formatjs.io/docs/core-concepts/icu-syntax"
      }}>{`message syntax`}</a>{` as the underlying `}<inlineCode parentName="p">{`react-intl`}</inlineCode>{` library.
Along with static text, this syntax supports variables, dates, and even conditional formatting.`}</p>
    <p>{`To translate text with variables, pass in a mapping object to the `}<inlineCode parentName="p">{`values`}</inlineCode>{` prop in the `}<inlineCode parentName="p">{`FormattedMessage`}</inlineCode>{` component.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`return (
  <FormattedMessage
    id="component.greeting"
    defaultMessage="Hi, {name}"
    values={{
      name: "Veronica",
    }}
  />
);
`}</code></pre>
    <p>{`When using the `}<inlineCode parentName="p">{`formatMessage()`}</inlineCode>{` function, pass in the mapping object as the second parameter.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`const text = formatMessage(
    { id:"component.greeting", defaultMessage="Hi, {name}"},
    { name: currentUser.firstname }
);
`}</code></pre>
    <p>{`For more details, see the `}<a parentName="p" {...{
        "href": "https://formatjs.io/docs/core-concepts/icu-syntax"
      }}>{`message syntax`}</a>{` documentation at FormatJS.`}</p>
    <h2 {...{
      "id": "language-packages-and-plugins"
    }}>{`Language packages and plugins`}</h2>
    <p>{`Language packages provide translation data for one or more locales.
They are also used to override the text in the same locale.`}</p>
    <p>{`Unlike the Adobe Commerce and Magento Open Source applications, which install language packages through Composer, PWA Studio storefronts install language packages as NPM dependencies.`}</p>
    <p>{`An NPM dependency is a language package if it meets the following criteria:`}</p>
    <ul>
      <li parentName="ul">{`The package contains an intercept file`}</li>
      <li parentName="ul">{`The intercept file sets the special feature `}<inlineCode parentName="li">{`i18n`}</inlineCode>{` flag to `}<inlineCode parentName="li">{`true`}</inlineCode>{` for the package`}</li>
      <li parentName="ul">{`The package contains an `}<inlineCode parentName="li">{`i18n`}</inlineCode>{` directory`}</li>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`i18n`}</inlineCode>{` directory contains a dictionary file with a locale formatted name`}</li>
    </ul>
    {/* TODO: Create an in-depth tutorial for creating a language package extension */}
    <h2 {...{
      "id": "build-process"
    }}>{`Build process`}</h2>
    <p>{`To optimize runtime performance, the i18n feature compiles all the translation data during the build process.`}</p>
    <p>{`The following is a high level summary of the actions the i18n feature takes to compile the translation data:`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`Scans all `}<inlineCode parentName="p">{`i18n`}</inlineCode>{` folders within installed modules that declare `}<inlineCode parentName="p">{`i18n`}</inlineCode>{` support through the `}<inlineCode parentName="p">{`specialFeatures`}</inlineCode>{` flag`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Scans the project's root directory for an `}<inlineCode parentName="p">{`i18n`}</inlineCode>{` directory`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Generates an object with locales as keys that map to an array of files matching that locale`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Merges the files in the arrays to create a single dictionary object for a locale`}</p>
        <p parentName="li"><strong parentName="p">{`NOTE:`}</strong>{` The dictionary files in the project itself are the last files merged and the final overrides for translations.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Creates a virtual module from this object that exposes a `}<inlineCode parentName="p">{`__fetchLocaleData__`}</inlineCode>{` function`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Generates a dynamic import in the application for the virtual module`}</p>
      </li>
    </ol>
    <h2 {...{
      "id": "runtime-process"
    }}>{`Runtime process`}</h2>
    <p>{`During runtime, the `}<inlineCode parentName="p">{`LocaleProvider`}</inlineCode>{` component uses the `}<inlineCode parentName="p">{`__fetchLocaleData__`}</inlineCode>{` function to get the correct translation data for the current locale.`}</p>
    <p>{`If a components changes the value of the current locale during runtime, the application sends a GraphQL query to verify the new value.
Even if you install a language package plugin for a locale, you must enable the locale on the backend to use the translations in the storefront UI.`}</p>
    <p>{`For example, if you use a store switcher and you provide an `}<inlineCode parentName="p">{`i18n/fr_FR.json`}</inlineCode>{` file, you must enable the French locale in the backend application to make the store switcher work.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      